import { useState, useEffect } from 'react';
import { Container, Grid, Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import KPICard from '../components/KPICard';
import AIBriefingCard from '../components/AIBriefingCard';
import { getLatestForecast, getForecastDetails } from '../api/crmClient';

interface ForecastSummary {
  id: string;
  team_commit: number;
  ai_forecast: number;
  delta: number;
  justification: string;
  created_at: string;
}

interface ForecastDetail {
  id: string;
  forecast_id: string;
  deal_id: string;
  rep_probability: number;
  ai_probability: number;
  deal_value: number;
  risk_factors: string[];
  created_at: string;
}

export function ForecastPage() {
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState<ForecastSummary | null>(null);
  const [details, setDetails] = useState<ForecastDetail[]>([]);

  useEffect(() => {
    const fetchForecastData = async () => {
      try {
        setLoading(true);
        const forecastData = await getLatestForecast();
        setForecast(forecastData);

        if (forecastData?.id) {
          const detailsData = await getForecastDetails(forecastData.id);
          setDetails(detailsData);
        }
      } catch (error) {
        console.error('Error fetching forecast data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchForecastData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(0)}%`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Sales Forecast
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : forecast ? (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <KPICard
                title="Team Commit"
                value={formatCurrency(forecast.team_commit)}
                icon={<TrendingUpIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <KPICard
                title="AI Forecast"
                value={formatCurrency(forecast.ai_forecast)}
                icon={<TrendingUpIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <KPICard
                title="Delta"
                value={formatCurrency(forecast.delta)}
                icon={forecast.delta >= 0 ? <TrendingUpIcon fontSize="large" /> : <TrendingDownIcon fontSize="large" color="error" />}
              />
            </Grid>
          </Grid>

          <AIBriefingCard summary={forecast.justification} />

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Top Contributing Deals
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Deal Name</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Rep's Probability</TableCell>
                  <TableCell align="right">AI Probability</TableCell>
                  <TableCell>Risk Factors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.map((detail) => (
                  <TableRow key={detail.id}>
                    <TableCell component="th" scope="row">
                      {detail.deal_id}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(detail.deal_value)}</TableCell>
                    <TableCell align="right">{formatPercentage(detail.rep_probability)}</TableCell>
                    <TableCell align="right">{formatPercentage(detail.ai_probability)}</TableCell>
                    <TableCell>
                      {detail.risk_factors?.map((risk, index) => (
                        <Chip
                          key={index}
                          label={risk}
                          size="small"
                          color="error"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          No forecast data available. The AI forecasting agent may not have run yet.
        </Typography>
      )}
    </Container>
  );
} 